import { inject, Injectable, InjectionToken, makeEnvironmentProviders } from '@angular/core';
import { distinctUntilChanged, filter, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaixaManualJobService } from './baixa-manual-job.service';
import { BoletosEmitidosPeriodoJobService } from './boletos-emitidos-periodo-job.service';
import { CertidaoCadastralJobService } from './certidao-cadastral-job.service';
import { CertidaoNegativaDebitoJobService } from './certidao-negativa-debito-job.service';
import { CertidaoPositivaDebitoJobService } from './certidao-positiva-debito-job.service';
import { DebitosComDiferencaJobService } from './debitos-com-diferenca-job.service';
import { DebitosDiferencasGeradasJobService } from './debitos-diferencas-geradas-job.service';
import { DeclaracaoValoresPagosJobService } from './declaracao-valores-pagos-job.service';
import { ExtratoMensalJobService } from './extrato-mensal-job.service';
import { FavorecidosPeloRefisJobService } from './favorecidos-pelo-refis-job.service';
import { FichaFinanceiraJobService } from './ficha-financeira-job.service';
import { NotificacaoCobrancaJobService } from './notificacao-cobranca-job.service';
import { RecebimentoCartaoBancoJobService } from './recebimento-cartao-banco-job.service';
import { RecebimentoMesJobService } from './recebimento-mes-job.service';
import { RelatorioBoletoJobService } from './relatorio-boleto-job.service';
import { RelatorioCartaoJobService } from './relatorio-cartao-job.service';
import { RelatorioContabilJobService } from './relatorio-contabil-job.service';
import { RelatorioDividaAtivaJobService } from './relatorio-divida-ativa-job.service';
import { RelatorioPixJobService } from './relatorio-pix-job.service';
import { RelatorioSelecaoJobService } from './relatorio-selecao-job.service';
import { ReportJobService } from './report-job.service';
import { SituacaoDasEmpresasJobService } from './situacao-das-empresas-job.service';
import { AptosAoDescontoJobService } from './aptos-ao-desconto-job.service';
import { DescontoMedicoEmpresaPedidosDeDescontoJobService } from './desconto-medico-empresa-pedidos-de-desconto-job.service';
import { DebitosGeradosPorPeriodoJobService } from './debitos-gerados-por-periodo-job.service';
import { DebitosGeradosUsuarioJobService } from './debitos-gerados-usuario-job.service';
import { NegociacaoEmDiaJobService } from './negociacao-em-dia-job.service';
import { NegociacaoRelacaoNegociacaoJobService } from './negociacao-relacao-negociacao-job.service';
import { RelatorioCreditoJobService } from './relatorio-credito-job.service';
import { RelatorioInadimplentePjJobService } from './relatorio-inadimplente-pj-job.service';
import { RelatorioInadimplentesAnaliticoJobService } from './relatorio-inadimplentes-analitico-job.service';
import { RelatorioInadimplentesSinteticoJobService } from './relatorio-inadimplentes-sintetico-job.service';
import { RelatorioParcelamentoJobService } from './relatorio-parcelamento-job.service';
import { RepasseCotaParteJobService } from './repasse-cota-parte-job.service';
import { RetornosProcessadosJobService } from './retornos-processados-job.service';
import { RetornosRecusadosJobService } from './retornos-recusados-job.service';
import { CartaoRelatorioJobService } from './cartao-relatorio-job.service';
import { ValoresAReceberJobService } from './valores-a-receber-job.service';
import { RelatorioIsencaoJobService } from './relatorio-isencao-job.service';
import { FaixaCapitalSocialJobService } from './faixa-capital-social-job.service';
import { NotificacoesService } from './notificacoes.service';

export interface ReportJobToastPayload {
    jobId: string;
    status: string;
    errorMessage?: string;
}

export interface ReportJobToastSource {
    job$: Observable<ReportJobToastPayload | null>;
    nomeRelatorio?: string;
    rotaRelatorio?: string;
    linkLabel?: string;
}

export const REPORT_JOB_TOAST_SOURCES = new InjectionToken<ReportJobToastSource[]>('REPORT_JOB_TOAST_SOURCES');

export const provideReportJobToastSources = () =>
    makeEnvironmentProviders([
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(BaixaManualJobService).job$,
                nomeRelatorio: 'Baixa Manual',
                rotaRelatorio: '/arrecadacao/relatorios/baixa-manual'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(BoletosEmitidosPeriodoJobService).job$,
                nomeRelatorio: 'Boletos Emitidos por Período',
                rotaRelatorio: '/arrecadacao/relatorios/boletos-emitidos-periodo'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(CertidaoCadastralJobService).job$,
                nomeRelatorio: 'Certidão Cadastral',
                rotaRelatorio: '/arrecadacao/relatorios/emissao/certidao-cadastral'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(CertidaoNegativaDebitoJobService).job$,
                nomeRelatorio: 'Certidão Negativa de Débito',
                rotaRelatorio: '/arrecadacao/relatorios/emissao/certidao-negativa-debito'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(CertidaoPositivaDebitoJobService).job$,
                nomeRelatorio: 'Certidão Positiva de Débito',
                rotaRelatorio: '/arrecadacao/relatorios/emissao/certidao-positiva-debito'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(DebitosComDiferencaJobService).job$,
                nomeRelatorio: 'Débitos com Diferença',
                rotaRelatorio: '/arrecadacao/relatorios/debitos-com-diferenca'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(DebitosDiferencasGeradasJobService).job$,
                nomeRelatorio: 'Débitos com Diferenças Geradas',
                rotaRelatorio: '/arrecadacao/relatorios/debitos-diferencas-geradas'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(DeclaracaoValoresPagosJobService).job$,
                nomeRelatorio: 'Declaração de Valores Pagos',
                rotaRelatorio: '/arrecadacao/relatorios/emissao/declaracao-valores-pagos'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(ExtratoMensalJobService).job$,
                nomeRelatorio: 'Extrato Mensal',
                rotaRelatorio: '/arrecadacao/relatorios/extrato-mensal'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(FavorecidosPeloRefisJobService).job$,
                nomeRelatorio: 'Favorecidos pelo REFIS',
                rotaRelatorio: '/arrecadacao/relatorios/listagem-valor/favorecidos-pelo-refis'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(FichaFinanceiraJobService).job$,
                nomeRelatorio: 'Ficha Financeira',
                rotaRelatorio: '/arrecadacao/relatorios/emissao/ficha-financeira'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(NotificacaoCobrancaJobService).job$,
                nomeRelatorio: 'Notificação de Cobrança',
                rotaRelatorio: '/arrecadacao/relatorios/notificacao-cobranca'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RecebimentoCartaoBancoJobService).job$,
                nomeRelatorio: 'Valor Recebido por Banco e Período',
                rotaRelatorio: '/arrecadacao/relatorios/recebimento/recebimento-cartao-banco'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RecebimentoMesJobService).job$,
                nomeRelatorio: 'Recebimento por Mês',
                rotaRelatorio: '/arrecadacao/relatorios/recebimento/recebimento-mes'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioBoletoJobService).job$,
                nomeRelatorio: 'Relatório Boleto',
                rotaRelatorio: '/arrecadacao/relatorios/listagem-valor/recebidos/boleto'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioCartaoJobService).job$,
                nomeRelatorio: 'Relatório Cartão',
                rotaRelatorio: '/arrecadacao/relatorios/listagem-valor/recebidos/cartao'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioContabilJobService).job$,
                nomeRelatorio: 'Relatório Contábil',
                rotaRelatorio: '/arrecadacao/relatorios/relatorio-contabil'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioDividaAtivaJobService).job$,
                nomeRelatorio: 'Relatório Dívida Ativa',
                rotaRelatorio: '/arrecadacao/relatorios/listagem-valor/recebidos/divida-ativa'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioPixJobService).job$,
                nomeRelatorio: 'Relatório Pix',
                rotaRelatorio: '/arrecadacao/relatorios/listagem-valor/recebidos/pix'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioSelecaoJobService).job$,
                nomeRelatorio: 'Relatório Seleção',
                rotaRelatorio: '/arrecadacao/relatorios/listagem-valor/a-receber/selecao'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(ReportJobService).job$,
                nomeRelatorio: 'Total por Anuidades',
                rotaRelatorio: '/arrecadacao/relatorios/listagem-valor/a-receber/total-por-anuidades'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(SituacaoDasEmpresasJobService).job$,
                nomeRelatorio: 'Situação das Empresas',
                rotaRelatorio: '/arrecadacao/relatorios/desconto-medico-empresa/situacao-das-empresas'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(AptosAoDescontoJobService).job$,
                nomeRelatorio: 'Aptos ao Desconto',
                rotaRelatorio: '/arrecadacao/relatorios/desconto-medico-empresa/aptos-ao-desconto'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(DescontoMedicoEmpresaPedidosDeDescontoJobService).job$,
                nomeRelatorio: 'Pedidos de Desconto',
                rotaRelatorio: '/arrecadacao/relatorios/desconto-medico-empresa/pedidos-de-desconto'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(DebitosGeradosPorPeriodoJobService).job$,
                nomeRelatorio: 'Débitos Gerados por Período',
                rotaRelatorio: '/arrecadacao/relatorios/debitos-gerados/periodo'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(DebitosGeradosUsuarioJobService).job$,
                nomeRelatorio: 'Débitos Gerados por Usuário',
                rotaRelatorio: '/arrecadacao/relatorios/debitos-gerados/usuario'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(NegociacaoEmDiaJobService).job$,
                nomeRelatorio: 'Negociação em Dia',
                rotaRelatorio: '/arrecadacao/relatorios/negociacao/negociacao-em-dia'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(NegociacaoRelacaoNegociacaoJobService).job$,
                nomeRelatorio: 'Relação de Negociação',
                rotaRelatorio: '/arrecadacao/relatorios/negociacao/relacao-negociacao'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioCreditoJobService).job$,
                nomeRelatorio: 'Relatório de Crédito',
                rotaRelatorio: '/arrecadacao/relatorios/relatorio-credito'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioInadimplentePjJobService).job$,
                nomeRelatorio: 'Inadimplentes Pessoa Jurídica',
                rotaRelatorio: '/arrecadacao/relatorios/inadimplentes/pessoa-juridica'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioInadimplentesAnaliticoJobService).job$,
                nomeRelatorio: 'Inadimplentes Analítico',
                rotaRelatorio: '/arrecadacao/relatorios/inadimplentes/analitico'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioInadimplentesSinteticoJobService).job$,
                nomeRelatorio: 'Inadimplentes Sintético',
                rotaRelatorio: '/arrecadacao/relatorios/inadimplentes/sintetico'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioParcelamentoJobService).job$,
                nomeRelatorio: 'Parcelamento',
                rotaRelatorio: '/arrecadacao/relatorios/parcelamento'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RepasseCotaParteJobService).job$,
                nomeRelatorio: 'Repasse Cota Parte',
                rotaRelatorio: '/arrecadacao/relatorios/repasse-cota-parte'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RetornosProcessadosJobService).job$,
                nomeRelatorio: 'Retornos Processados',
                rotaRelatorio: '/arrecadacao/relatorios/retornos/processados'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RetornosRecusadosJobService).job$,
                nomeRelatorio: 'Retornos Recusados',
                rotaRelatorio: '/arrecadacao/relatorios/retornos/retornos-recusados'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(CartaoRelatorioJobService).job$,
                nomeRelatorio: 'Relatório Cartão',
                rotaRelatorio: '/arrecadacao/relatorios/cartao'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(ValoresAReceberJobService).job$,
                nomeRelatorio: 'Valores a Receber',
                rotaRelatorio: '/arrecadacao/relatorios/listagem-valor/a-receber/valores-a-receber'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(RelatorioIsencaoJobService).job$,
                nomeRelatorio: 'Relatório de Isenção',
                rotaRelatorio: '/arrecadacao/relatorios/relatorio-isencao'
            })
        },
        {
            provide: REPORT_JOB_TOAST_SOURCES,
            multi: true,
            useFactory: () => ({
                job$: inject(FaixaCapitalSocialJobService).job$,
                nomeRelatorio: 'Faixa de Capital Social',
                rotaRelatorio: '/arrecadacao/relatorios/faixa-capital-social'
            })
        }
    ]);

@Injectable({ providedIn: 'root' })
export class ReportJobToastService {
    private readonly notificacoesService = inject(NotificacoesService);
    private readonly sources = inject(REPORT_JOB_TOAST_SOURCES, { optional: true }) ?? [];

    constructor() {
        this.sources.forEach((source) => {
            source.job$
                .pipe(
                    filter((job) => Boolean(job?.jobId) && (job?.status === 'DONE' || job?.status === 'ERROR')),
                    distinctUntilChanged((a, b) => a?.status === b?.status && a?.jobId === b?.jobId),
                    takeUntilDestroyed()
                )
                .subscribe((job) => this.handleJob(source, job));
        });
    }

    private handleJob(source: ReportJobToastSource, job: ReportJobToastPayload | null): void {
        if (!job) return;

        const nomeRelatorio = source.nomeRelatorio?.trim();

        if (job.status === 'DONE') {
            this.notificacoesService.notificar({
                severity: 'success',
                summary: 'Relatório pronto',
                detail: nomeRelatorio ? `${nomeRelatorio} está disponível.` : 'O relatório solicitado está disponível.',
                life: 10000,
                link: source.rotaRelatorio,
                linkLabel: source.linkLabel
            });
            return;
        }

        if (job.status === 'ERROR') {
            this.notificacoesService.notificar({
                severity: 'error',
                summary: 'Erro no relatório',
                detail: job.errorMessage ?? (nomeRelatorio ? `Falha ao gerar ${nomeRelatorio}.` : 'Falha ao gerar relatório.'),
                life: 10000
            });
        }
    }
}

export const initializeReportJobToasts = () => {
    inject(ReportJobToastService);
};
